import { Navbar, Footer } from './LandingPage';
import { WanderfulHero } from './WanderfulHero';
import { Download, FileText, BarChart3, PieChart, CheckCircle2 } from 'lucide-react';

const SampleReportsPage = () => {
  const reports = [
    {
      title: "Individual Student Report (PDF)",
      description: "A detailed breakdown of marks, question-wise performance, and qualitative feedback for a single student.",
      features: ["Handwriting overlay", "Question-wise marks", "Step-marking details", "Teacher comments section"],
      icon: <FileText className="w-10 h-10 text-black" />,
      fileSize: "2.4 MB"
    },
    {
      title: "Batch Analytics Dashboard (Excel)",
      description: "High-level data for an entire batch of students, perfect for identifying topic-wise weak areas.",
      features: ["Average scores", "Highest/Lowest marks", "Topic-wise performance", "Raw marks export"],
      icon: <BarChart3 className="w-10 h-10 text-black" />,
      fileSize: "1.1 MB"
    },
    {
      title: "Comparison Report (PDF)",
      description: "See how Parakh AI's marking compares to human faculty marking with error flags highlighted.",
      features: ["Side-by-side comparison", "Error flagging", "Audit trail", "Consistency score"],
      icon: <PieChart className="w-10 h-10 text-black" />,
      fileSize: "3.8 MB"
    }
  ];

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <WanderfulHero 
        title="Experience the Depth of Parakh Analytics."
        subtitle="Download sample reports to see how we provide actionable insights beyond just marks."
        cta1Text="Try it for Free"
      />

      <section className="py-8 sm:py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <span className="text-sm font-bold text-slate-900 uppercase tracking-widest bg-gray-100 px-4 py-1.5 rounded-full">Output Samples</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-black mt-4">Download Sample Reports</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-4 font-medium">See the detailed feedback and analytics your students and faculty will receive.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {reports.map((report, index) => (
              <div key={index} className="bg-[#f9f9f9] rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-4 sm:p-6 md:p-8 border border-transparent hover:border-gray-200 hover:bg-white hover:shadow-xl transition-all duration-500 flex flex-col">
                <div className="bg-white w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl shadow-sm flex items-center justify-center mb-6 sm:mb-8">
                  {report.icon}
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-black mb-4">{report.title}</h3>
                <p className="text-gray-600 mb-8 flex-grow">{report.description}</p>
                
                <div className="space-y-3 mb-8">
                  {report.features.map((feat, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-500">
                      <CheckCircle2 className="w-4 h-4 text-black" /> {feat}
                    </div>
                  ))}
                </div>

                <button className="w-full bg-black text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 group hover:bg-gray-800 transition-colors">
                  <Download className="w-4 h-4 group-hover:-translate-y-1 transition-transform" /> 
                  Download Sample <span className="text-white/40 text-xs ml-1">({report.fileSize})</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Preview Section */}
      <section className="pb-8 sm:pb-12 md:pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
           <div className="bg-black text-white rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-6 sm:p-8 md:p-12 lg:p-20 text-center relative overflow-hidden">
             {/* Decorative background elements */}
             <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
               <div className="absolute top-10 left-10 w-64 h-64 border border-white rounded-full"></div>
               <div className="absolute bottom-10 right-10 w-96 h-96 border border-white rounded-full"></div>
             </div>

             <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-8">Want to see your own papers?</h2>
             <p className="text-white/60 text-lg mb-12 max-w-2xl mx-auto">
               Upload 5 answer sheets from your last test series and we'll generate a comprehensive evaluation report for free.
             </p>
             <button className="bg-white text-black font-bold px-10 py-4 rounded-full hover:scale-105 transition-all text-lg">
               Start Your Free Trial
             </button>
           </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SampleReportsPage;
